import {
  OpenModal,
  CloseModal,
  RefreshModalConfig,
  CloseAllModals,
  KillModal,
} from 'data/Modals/actions'
import { selectAllModals } from 'data/Modals/selectors'
import { TModalConfig, TModalIdentifier } from 'models/Modals'
import { useDispatch, useSelector } from 'react-redux'

/**
 * Custom hook to manage modal actions and lifecycle callbacks in a centralized way.
 *
 * Provides functions to open, close, kill, refresh, and close all modals
 * with proper handling of lifecycle callbacks like `onClose` and `onExited`.
 *
 * This abstraction helps keep modal logic consistent and prevents duplication
 * of callback invocations across components.
 *
 * Usage:
 * Call these functions to control modals programmatically while ensuring
 * consistent lifecycle event handling.
 */
export const useModalsManager = () => {
  const modals = useSelector(selectAllModals)
  const dispatch = useDispatch()

  /**
   * Opens a new modal or re-opens an existing one with the given configuration.
   *
   * If a modal with the same `identifier` already exists, it will be updated and shown.
   * Use this to programmatically launch modals with custom content, actions, and behavior.
   *
   * @param config - The full configuration object for the modal (must include a unique `identifier`).
   */
  const openModal = (config: TModalConfig) => {
    dispatch(OpenModal(config))
  }

  /**
   * Closes the modal and triggers its `onClose` callback (if defined).
   */
  const closeModal = (identifier: TModalIdentifier) => {
    const modal = modals[identifier]
    modal?.onClose?.()
    dispatch(CloseModal(identifier))
  }

  /**
   * Closes the modal without triggering `onClose`.
   * @param identifier - The unique identifier of the modal to update.
   */
  const silentCloseModal = (identifier: TModalIdentifier) => {
    dispatch(CloseModal(identifier))
  }

  /**
   * Removes the modal from Redux and triggers its `onExited` callback (if defined).
   * @param identifier - The unique identifier of the modal to update.
   */
  const killModal = (identifier: TModalIdentifier) => {
    const modal = modals[identifier]
    modal?.onExited?.() // Call cleanup callback before removing
    dispatch(KillModal(identifier))
  }

  /**
   * Immediately removes the modal from Redux without calling `onExited`.
   * @param identifier - The unique identifier of the modal to update.
   */
  const silentKillModal = (identifier: TModalIdentifier) => {
    dispatch(KillModal(identifier))
  }

  /**
   * Updates the configuration of an existing modal.
   *
   * Use this to dynamically modify modal properties such as `isLoading`, `title`, `content`, etc.
   * Useful during async flows (e.g., show loading spinner during confirm actions).
   *
   * @param identifier - The unique identifier of the modal to update.
   * @param config - A partial modal config object with the updated fields.
   */
  const refreshModalConfig = (identifier: TModalIdentifier, config: Partial<TModalConfig>) => {
    dispatch(RefreshModalConfig({ identifier, ...config }))
  }

  /**
   * Closes all modals and triggers their `onClose` callbacks (if defined).
   */
  const closeAllModals = () => {
    // Trigger any onClose hooks before closing all
    Object.values(modals).forEach((modal) => {
      modal.onClose?.()
    })

    dispatch(CloseAllModals())
  }

  /**
   * Immediately closes all modals without calling `onClose`.
   */
  const silentCloseAllModals = () => {
    dispatch(CloseAllModals())
  }

  return {
    openModal,
    closeModal,
    silentCloseModal,
    killModal,
    silentKillModal,
    refreshModalConfig,
    closeAllModals,
    silentCloseAllModals,
  }
}
